import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  ArrowPathIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  UsersIcon,
  AdjustmentsHorizontalIcon,
  CheckBadgeIcon,
} from '@heroicons/react/24/outline';
import { getAdminUsers, getAdminUserById } from '../api/adminUsers';
import { parseServerDate } from '../utils/time';
import UserDetailsPanel, {
  USER_TYPES,
  TypeBadge,
  UserAvatar,
  roleLabel,
  typeMeta,
} from '../components/users/UserDetailsPanel';

const STATUS_OPTIONS = [
  { value: '', label: 'Все статусы' },
  { value: 'true', label: 'Активные' },
  { value: 'false', label: 'Деактивированные' },
];

const PAGE_SIZE = 10;
const SORT_OPTIONS = [
  { value: 'created_desc', label: 'Сначала новые' },
  { value: 'created_asc', label: 'Сначала старые' },
  { value: 'active_desc', label: 'Недавно активные' },
  { value: 'balance_desc', label: 'Больший баланс' },
  { value: 'rating_desc', label: 'Высокий рейтинг' },
  { value: 'name_asc', label: 'По имени (А–Я)' },
];

const ACTIVITY_OPTIONS = [
  { value: '', label: 'Любая активность' },
  { value: '1d', label: 'Заходили за 24 ч' },
  { value: '7d', label: 'Заходили за 7 дней' },
  { value: '30d', label: 'Заходили за 30 дней' },
  { value: 'inactive30', label: 'Не заходили 30+ дней' },
  { value: 'never', label: 'Нет данных об активности' },
];

const EMPTY_FILTERS = {
  id: '',
  q: '',
  email: '',
  phone_number: '',
  user_type: '',
  is_active: '',
  is_verified: '',
  created_from: '',
  created_to: '',
  activity: '',
  sort: 'created_desc',
};

const fmtShortDate = (iso) => (iso ? parseServerDate(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' }) : null);
const ago = (iso) => {
  if (!iso) return null;
  const m = Math.floor((Date.now() - parseServerDate(iso).getTime()) / 60000);
  if (m < 5) return 'только что';
  if (m < 60) return `${m} мин назад`;
  if (m < 1440) return `${Math.floor(m / 60)} ч назад`;
  if (m < 43200) return `${Math.floor(m / 1440)} дн назад`;
  return fmtShortDate(iso);
};

const inputCls =
  'w-full rounded-lg border border-border-primary bg-bg-primary px-3 py-2 text-sm text-text-primary ' +
  'placeholder:text-text-muted focus:border-[#4F45E5] focus:outline-none focus:ring-2 focus:ring-[#4F45E5]/30';

const formatMoney = (v) => `${Number(v || 0).toLocaleString('ru-RU')} ₸`;

const SkeletonRow = () => (
  <li className="flex animate-pulse items-center gap-3 px-4 py-3">
    <div className="h-10 w-10 rounded-full bg-bg-tertiary" />
    <div className="flex-1 space-y-2">
      <div className="h-3 w-1/3 rounded bg-bg-tertiary" />
      <div className="h-3 w-1/2 rounded bg-bg-tertiary" />
    </div>
    <div className="h-3 w-16 rounded bg-bg-tertiary" />
  </li>
);

// Переход из других разделов (напр. «Заказы») — /users?id=123 сразу открывает профиль
const initialIdParam = () => (new URLSearchParams(window.location.search).get('id') || '').replace(/\D/g, '');

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [selectedId, setSelectedId] = useState(() => (initialIdParam() ? Number(initialIdParam()) : null));
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [toast, setToast] = useState(null);
  const [showExact, setShowExact] = useState(false);
  const [formFilters, setFormFilters] = useState(() => ({ ...EMPTY_FILTERS, id: initialIdParam() }));
  const [appliedFilters, setAppliedFilters] = useState(() => ({ ...EMPTY_FILTERS, id: initialIdParam() }));
  const detailsRef = useRef(null);

  const notify = useCallback((type, text) => setToast({ type, text }), []);

  useEffect(() => {
    if (!toast || toast.type === 'error') return undefined;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const idQuery = appliedFilters.id.trim();
      if (idQuery) {
        // Поиск по ID через /admin/user/{id}
        try {
          const user = await getAdminUserById(idQuery);
          setUsers(user ? [user] : []);
          setTotalUsers(user ? 1 : 0);
        } catch (error) {
          if (error.status !== 404) throw error;
          setUsers([]);
          setTotalUsers(0);
        }
        setTotalPages(1);
        setHasMore(false);
        return;
      }

      const params = { page, limit: PAGE_SIZE };
      if (appliedFilters.q.trim()) params.q = appliedFilters.q.trim();
      if (appliedFilters.email.trim()) params.email = appliedFilters.email.trim();
      if (appliedFilters.phone_number.trim()) params.phone_number = appliedFilters.phone_number.trim();
      if (appliedFilters.user_type) params.user_type = appliedFilters.user_type;
      if (appliedFilters.is_active) params.is_active = appliedFilters.is_active === 'true';
      if (appliedFilters.is_verified) params.is_verified = appliedFilters.is_verified === 'true';
      if (appliedFilters.created_from) params.created_from = appliedFilters.created_from;
      if (appliedFilters.created_to) params.created_to = appliedFilters.created_to;
      if (appliedFilters.activity) params.activity = appliedFilters.activity;
      params.sort = appliedFilters.sort;
      const data = await getAdminUsers(params);
      const items = data.items || [];
      setUsers(items);
      if (typeof data.total === 'number') {
        const pages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));
        setTotalUsers(data.total);
        setTotalPages(pages);
        setHasMore(page < pages);
      } else {
        // Старый backend не отдаёт total — следующую страницу определяем по размеру ответа
        setTotalUsers(null);
        setTotalPages(null);
        setHasMore(items.length === PAGE_SIZE);
      }
    } catch (error) {
      setUsers([]);
      setHasMore(false);
      notify('error', `Ошибка загрузки пользователей: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [page, appliedFilters, notify]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const applyFilters = (next) => {
    setToast(null);
    setFormFilters(next);
    setAppliedFilters(next);
    setPage(1);
  };

  const handleSearch = (e) => {
    e?.preventDefault();
    applyFilters({ ...formFilters });
  };

  const handleResetFilters = () => {
    setShowExact(false);
    applyFilters(EMPTY_FILTERS);
  };

  const removeFilter = (key) =>
    applyFilters(key === 'created' ? { ...appliedFilters, created_from: '', created_to: '' } : { ...appliedFilters, [key]: '' });

  const openUser = useCallback((userId) => {
    setSelectedId(userId);
    // На мобильных панель деталей под списком — прокручиваем к ней
    if (window.innerWidth < 1024) {
      setTimeout(() => detailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    }
  }, []);

  const closeDetails = useCallback(() => setSelectedId(null), []);

  // Изменения из панели сразу отражаются в строке списка
  const handleUpdated = useCallback((updated) => {
    setUsers((list) => list.map((u) => (u.id === updated.id ? { ...u, ...updated } : u)));
  }, []);

  const activeChips = [
    appliedFilters.id && { key: 'id', label: `ID: ${appliedFilters.id}` },
    appliedFilters.q && { key: 'q', label: `«${appliedFilters.q}»` },
    appliedFilters.email && { key: 'email', label: `Email: ${appliedFilters.email}` },
    appliedFilters.phone_number && { key: 'phone_number', label: `Тел: ${appliedFilters.phone_number}` },
    appliedFilters.user_type && { key: 'user_type', label: typeMeta(appliedFilters.user_type).label },
    appliedFilters.is_active && {
      key: 'is_active',
      label: STATUS_OPTIONS.find((o) => o.value === appliedFilters.is_active)?.label,
    },
    (appliedFilters.created_from || appliedFilters.created_to) && {
      key: 'created',
      label: `Регистрация${appliedFilters.created_from ? ` с ${new Date(appliedFilters.created_from).toLocaleDateString('ru-RU')}` : ''}${
        appliedFilters.created_to ? ` по ${new Date(appliedFilters.created_to).toLocaleDateString('ru-RU')}` : ''
      }`,
    },
    appliedFilters.activity && { key: 'activity', label: ACTIVITY_OPTIONS.find((o) => o.value === appliedFilters.activity)?.label },
    appliedFilters.is_verified && { key: 'is_verified', label: appliedFilters.is_verified === 'true' ? 'Верифицированные' : 'Без верификации' },
  ].filter(Boolean);

  const rangeStart = (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = (page - 1) * PAGE_SIZE + users.length;
  const idSearchActive = Boolean(appliedFilters.id);

  return (
    <div className="space-y-4 text-left">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-text-primary">Пользователи</h2>
          <p className="mt-0.5 text-sm text-text-secondary">
            {totalUsers !== null ? `Найдено: ${totalUsers.toLocaleString('ru-RU')}` : 'Управление аккаунтами и ролями'}
          </p>
        </div>
        <button
          type="button"
          onClick={loadUsers}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-lg border border-border-primary px-3 py-2 text-sm font-medium text-text-secondary hover:bg-bg-tertiary disabled:opacity-50"
        >
          <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Обновить
        </button>
      </div>

      {/* Filters */}
      <form onSubmit={handleSearch} className="rounded-block border border-border-primary bg-bg-card p-4 shadow-sm">
        <div className="flex flex-col gap-3 md:flex-row">
          <div className="relative md:w-32">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-text-muted">#</span>
            <input
              type="text"
              inputMode="numeric"
              placeholder="ID"
              aria-label="ID пользователя"
              value={formFilters.id}
              onChange={(e) => setFormFilters((f) => ({ ...f, id: e.target.value.replace(/\D/g, '') }))}
              className={`${inputCls} pl-7`}
            />
          </div>
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
            <input
              type="text"
              placeholder="Имя, email или телефон…"
              aria-label="Поиск по имени, email или телефону"
              value={formFilters.q}
              onChange={(e) => setFormFilters((f) => ({ ...f, q: e.target.value }))}
              className={`${inputCls} pl-9`}
            />
          </div>
          <select
            aria-label="Тип пользователя"
            value={formFilters.user_type}
            onChange={(e) => applyFilters({ ...formFilters, user_type: e.target.value })}
            className={`${inputCls} md:w-44`}
          >
            <option value="">Все типы</option>
            {USER_TYPES.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <select
            aria-label="Статус аккаунта"
            value={formFilters.is_active}
            onChange={(e) => applyFilters({ ...formFilters, is_active: e.target.value })}
            className={`${inputCls} md:w-44`}
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <div className="flex gap-2">
            <button
              type="submit"
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-accent-grad px-4 py-2 text-sm font-semibold text-white hover:opacity-90 md:flex-none"
            >
              <MagnifyingGlassIcon className="h-4 w-4" />
              Искать
            </button>
            <button
              type="button"
              onClick={() => setShowExact((v) => !v)}
              title="Точный поиск"
              aria-expanded={showExact}
              className={`inline-flex items-center gap-1 rounded-lg border px-3 py-2 text-sm font-medium ${
                showExact
                  ? 'border-[#4F45E5]/50 bg-[#4F45E5]/10 text-[#7C3AED]'
                  : 'border-border-primary text-text-secondary hover:bg-bg-tertiary'
              }`}
            >
              <AdjustmentsHorizontalIcon className="h-4 w-4" />
              <ChevronDownIcon className={`h-3 w-3 transition-transform ${showExact ? 'rotate-180' : ''}`} />
            </button>
          </div>
        </div>

        {showExact && (
          <div className="mt-3 grid grid-cols-1 gap-3 border-t border-border-primary pt-3 sm:grid-cols-2 lg:grid-cols-4">
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-text-secondary">Зарегистрирован с</span>
              <input
                type="date"
                value={formFilters.created_from}
                max={formFilters.created_to || undefined}
                onChange={(e) => setFormFilters((f) => ({ ...f, created_from: e.target.value }))}
                className={inputCls}
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-text-secondary">Зарегистрирован по</span>
              <input
                type="date"
                value={formFilters.created_to}
                min={formFilters.created_from || undefined}
                onChange={(e) => setFormFilters((f) => ({ ...f, created_to: e.target.value }))}
                className={inputCls}
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-text-secondary">Активность</span>
              <select value={formFilters.activity} onChange={(e) => applyFilters({ ...formFilters, activity: e.target.value })} className={inputCls}>
                {ACTIVITY_OPTIONS.map((o) => (
                  <option key={o.value || 'any'} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-text-secondary">Верификация (KYC)</span>
              <select value={formFilters.is_verified} onChange={(e) => applyFilters({ ...formFilters, is_verified: e.target.value })} className={inputCls}>
                <option value="">Все</option>
                <option value="true">Верифицированные</option>
                <option value="false">Без верификации</option>
              </select>
            </label>
            <label className="block lg:col-span-2">
              <span className="mb-1 block text-xs font-medium text-text-secondary">Email — точное совпадение</span>
              <input
                type="email"
                placeholder="test@example.com"
                value={formFilters.email}
                onChange={(e) => setFormFilters((f) => ({ ...f, email: e.target.value }))}
                className={inputCls}
              />
            </label>
            <label className="block lg:col-span-2">
              <span className="mb-1 block text-xs font-medium text-text-secondary">Телефон — точное совпадение</span>
              <input
                type="tel"
                placeholder="+77011234567"
                value={formFilters.phone_number}
                onChange={(e) => setFormFilters((f) => ({ ...f, phone_number: e.target.value }))}
                className={inputCls}
              />
            </label>
          </div>
        )}

        {activeChips.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {activeChips.map((chip) => (
              <span
                key={chip.key}
                className="inline-flex items-center gap-1 rounded-full border border-[#4F45E5]/30 bg-[#4F45E5]/10 py-0.5 pl-3 pr-1 text-xs font-medium text-[#7C3AED] dark:text-indigo-300"
              >
                {chip.label}
                <button
                  type="button"
                  onClick={() => removeFilter(chip.key)}
                  aria-label={`Убрать фильтр ${chip.label}`}
                  className="rounded-full p-0.5 hover:bg-[#4F45E5]/20"
                >
                  <XMarkIcon className="h-3.5 w-3.5" />
                </button>
              </span>
            ))}
            <button
              type="button"
              onClick={handleResetFilters}
              className="text-xs font-medium text-text-secondary underline-offset-2 hover:text-text-primary hover:underline"
            >
              Сбросить всё
            </button>
          </div>
        )}
      </form>

      {toast && (
        <div
          role={toast.type === 'error' ? 'alert' : 'status'}
          className={`flex items-start justify-between gap-3 rounded-lg border px-4 py-3 text-sm ${
            toast.type === 'error'
              ? 'border-rose-500/30 bg-rose-500/10 text-rose-500'
              : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-500'
          }`}
        >
          <span>{toast.text}</span>
          <button type="button" onClick={() => setToast(null)} aria-label="Закрыть" className="shrink-0 opacity-70 hover:opacity-100">
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-5">
        {/* Users list */}
        <section className="overflow-hidden rounded-block border border-border-primary bg-bg-card shadow-sm lg:col-span-3">
          {!idSearchActive && (
            <div className="flex items-center justify-between gap-3 border-b border-border-primary px-4 py-2">
              <span className="text-xs text-text-muted">{totalUsers !== null ? `${totalUsers.toLocaleString('ru-RU')} пользователей` : ''}</span>
              <label className="flex items-center gap-2 text-xs text-text-secondary">
                Сортировка
                <select
                  value={appliedFilters.sort}
                  onChange={(e) => applyFilters({ ...appliedFilters, sort: e.target.value })}
                  aria-label="Сортировка"
                  className="rounded-lg border border-border-primary bg-bg-primary px-2 py-1 text-xs font-medium text-text-primary focus:border-[#4F45E5] focus:outline-none"
                >
                  {SORT_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </label>
            </div>
          )}
          <ul className="divide-y divide-border-primary">
            {loading && Array.from({ length: users.length || 6 }).map((_, i) => <SkeletonRow key={i} />)}

            {!loading && users.length === 0 && (
              <li className="px-6 py-16 text-center">
                <UsersIcon className="mx-auto mb-3 h-12 w-12 text-text-muted" />
                <h3 className="mb-1 text-base font-semibold text-text-primary">Пользователи не найдены</h3>
                <p className="mb-5 text-sm text-text-secondary">
                  {idSearchActive ? `Пользователя с ID ${appliedFilters.id} не существует.` : 'Попробуйте изменить параметры поиска.'}
                </p>
                {activeChips.length > 0 && (
                  <button
                    type="button"
                    onClick={handleResetFilters}
                    className="rounded-lg border border-border-primary px-4 py-2 text-sm font-medium text-text-secondary hover:bg-bg-tertiary"
                  >
                    Сбросить фильтры
                  </button>
                )}
              </li>
            )}

            {!loading &&
              users.map((user) => {
                const selected = selectedId === user.id;
                const inactive = user.is_active === false;
                return (
                  <li key={user.id}>
                    <button
                      type="button"
                      onClick={() => openUser(user.id)}
                      aria-current={selected ? 'true' : undefined}
                      className={`relative flex w-full items-center gap-3 px-4 py-3 text-left transition-colors ${
                        selected ? 'bg-[#4F45E5]/[0.08]' : 'hover:bg-bg-tertiary/60'
                      }`}
                    >
                      {selected && <span className="absolute inset-y-0 left-0 w-1 bg-accent-grad" />}
                      <div className={inactive ? 'opacity-40 grayscale' : ''}>
                        <UserAvatar user={user} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <span className={`truncate text-sm font-semibold ${inactive ? 'text-text-muted line-through' : 'text-text-primary'}`}>
                            {user.name || <span className="italic text-text-muted">Без имени</span>}
                          </span>
                          {user.is_verified && <CheckBadgeIcon className="h-4 w-4 shrink-0 text-sky-500" title="Верифицирован" />}
                          <span className="shrink-0 font-mono text-xs text-text-muted">#{user.id}</span>
                          {inactive && (
                            <span className="shrink-0 rounded border border-rose-500/30 bg-rose-500/10 px-1.5 py-px text-[10px] font-semibold text-rose-500">
                              деактивирован
                            </span>
                          )}
                        </div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-text-secondary">
                          <span>{user.phone_number || 'Телефон не указан'}</span>
                          {user.email && <span className="truncate">{user.email}</span>}
                        </div>
                      </div>
                      <div className="hidden shrink-0 flex-col items-end gap-1 sm:flex">
                        <div className="flex gap-1">
                          <TypeBadge type={user.user_type} />
                          {user.user_type === 'business' && user.business_role && (
                            <span className="inline-flex items-center rounded-md border border-blue-500/30 px-2 py-0.5 text-xs font-medium text-blue-400">
                              {roleLabel(user.business_role)}
                            </span>
                          )}
                        </div>
                        <span className="text-[11px] text-text-muted">
                          {appliedFilters.sort === 'active_desc' || appliedFilters.activity
                            ? user.last_active_at
                              ? `был(а) ${ago(user.last_active_at)}`
                              : 'нет данных об активности'
                            : user.created_at && `рег. ${fmtShortDate(user.created_at)}`}
                        </span>
                        <span className="text-xs text-text-secondary">
                          <span className="font-medium text-emerald-500">{formatMoney(user.balance)}</span>
                          {' · '}★ {Number(user.rating || 0).toFixed(1)}
                        </span>
                      </div>
                    </button>
                  </li>
                );
              })}
          </ul>

          {/* Pagination */}
          {!idSearchActive && (users.length > 0 || page > 1) && (
            <div className="flex items-center justify-between gap-3 border-t border-border-primary px-4 py-3">
              <span className="text-xs text-text-secondary">
                {users.length > 0 ? `${rangeStart}–${rangeEnd}` : '0'}
                {totalUsers !== null && ` из ${totalUsers.toLocaleString('ru-RU')}`}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1 || loading}
                  aria-label="Предыдущая страница"
                  className="rounded-lg border border-border-primary p-1.5 text-text-secondary hover:bg-bg-tertiary disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                </button>
                <span className="min-w-[4.5rem] text-center text-sm font-medium text-text-primary">
                  {page}{totalPages ? ` / ${totalPages}` : ''}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={!hasMore || loading}
                  aria-label="Следующая страница"
                  className="rounded-lg border border-border-primary p-1.5 text-text-secondary hover:bg-bg-tertiary disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ChevronRightIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </section>

        {/* User details */}
        <aside ref={detailsRef} className="lg:col-span-2">
          <div className="overflow-hidden rounded-block border border-border-primary bg-bg-card shadow-sm lg:sticky lg:top-4 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto">
            {selectedId ? (
              <UserDetailsPanel
                key={selectedId}
                userId={selectedId}
                onClose={closeDetails}
                onUpdated={handleUpdated}
                onOpenUser={openUser}
                notify={notify}
              />
            ) : (
              <div className="px-6 py-16 text-center">
                <UsersIcon className="mx-auto mb-3 h-10 w-10 text-text-muted" />
                <p className="text-sm text-text-secondary">Выберите пользователя, чтобы увидеть все данные</p>
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
};

export default UserManagement;
